import type { GameAction } from '../engine/types';
import type { TableDefinition } from '../engine/tables/definition';
import { createRequestAbortHandle } from './requestAbort';

export interface CommunityPlayer {
  id: string;
  initials: string;
  email: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  totalPlayMs: number;
  totalGames: number;
}

export interface CommunityConfig {
  communityEnabled: boolean;
  googleClientId: string | null;
}

export interface PlayerPreferencesPayload {
  renderMode?: '2d' | '3d';
  cameraMode?: 'normal' | 'overhead' | 'close' | 'follow' | 'chase';
  masterVolume?: number;
  muted?: boolean;
  effectsEnabled?: boolean;
  attractMotion?: boolean;
  selectedTableId?: string | null;
  keymap?: Partial<Record<GameAction, string>>;
}

export interface CommunityTableSummary {
  id: string;
  name: string;
  description: string;
  visibility: 'private' | 'unlisted' | 'public';
  plays: number;
  is_official: number;
  created_at: number;
  updated_at: number;
  published_version_id: string | null;
  forked_from_table_id: string | null;
  author_name: string | null;
  author_initials: string | null;
}

export interface CommunityTableDetail {
  id: string;
  name: string;
  description: string;
  visibility: 'private' | 'unlisted' | 'public';
  plays: number;
  published_version_id: string | null;
  forked_from_table_id: string | null;
  author_name: string | null;
  author_initials: string | null;
  definition: TableDefinition;
  versionId: string | null;
  versionNumber: number | null;
  canEdit: boolean;
}

export interface LeaderboardEntry {
  rank: number;
  playerId: string;
  initials: string;
  displayName: string | null;
  avatarUrl: string | null;
  score: number;
  finishedAt: number;
}

export interface TableLeaderboardData {
  tableId: string;
  versionId: string;
  versionNumber: number;
  entries: LeaderboardEntry[];
}

export class CommunityApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'CommunityApiError';
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const abort = createRequestAbortHandle(init?.signal);
  try {
    const response = await fetch(path, {
      credentials: 'same-origin',
      ...init,
      signal: abort.signal,
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
    const body = (await response.json().catch(() => null)) as
      | (T & { ok?: boolean })
      | { error?: { code?: string; message?: string } }
      | null;
    if (!response.ok) {
      const errorBody = body as { error?: { code?: string; message?: string } } | null;
      throw new CommunityApiError(
        response.status,
        errorBody?.error?.code ?? 'request_failed',
        errorBody?.error?.message ?? `Community request failed with ${response.status}.`,
      );
    }
    return body as T;
  } catch (error) {
    if (abort.didTimeout()) {
      throw new CommunityApiError(408, 'request_timeout', 'Community request timed out. Please try again.');
    }
    throw error;
  } finally {
    abort.cleanup();
  }
}

export async function getCommunityConfig(): Promise<CommunityConfig> {
  const result = await request<{ ok: true } & CommunityConfig>('/api/config');
  return { communityEnabled: result.communityEnabled, googleClientId: result.googleClientId };
}

export async function getMe(): Promise<CommunityPlayer | null> {
  const result = await request<{ ok: true; player: CommunityPlayer | null }>('/api/auth/me');
  return result.player;
}

export async function loginWithGoogle(credential: string): Promise<CommunityPlayer> {
  const result = await request<{ ok: true; player: CommunityPlayer }>('/api/auth/google', {
    method: 'POST',
    body: JSON.stringify({ credential }),
  });
  return result.player;
}

export async function logout(): Promise<void> {
  await request<{ ok: true }>('/api/auth/logout', { method: 'POST', body: '{}' });
}

export async function getPlayerPreferences(): Promise<PlayerPreferencesPayload | null> {
  const result = await request<{ ok: true; preferences: PlayerPreferencesPayload | null }>('/api/preferences');
  return result.preferences;
}

export async function savePlayerPreferences(preferences: PlayerPreferencesPayload): Promise<PlayerPreferencesPayload> {
  const result = await request<{ ok: true; preferences: PlayerPreferencesPayload }>('/api/preferences', {
    method: 'PUT',
    body: JSON.stringify({ preferences }),
  });
  return result.preferences;
}

export async function listCommunityTables(
  scope: 'public' | 'mine' = 'public',
  sort: 'popular' | 'new' = 'popular',
): Promise<CommunityTableSummary[]> {
  const params = new URLSearchParams({ scope, limit: '24' });
  if (scope === 'public') params.set('sort', sort);
  const result = await request<{ ok: true; tables: CommunityTableSummary[] }>(`/api/tables?${params.toString()}`);
  return result.tables;
}

export async function getCommunityTable(id: string, draft = false): Promise<CommunityTableDetail> {
  const result = await request<{ ok: true; table: CommunityTableDetail }>(
    `/api/tables/${encodeURIComponent(id)}${draft ? '?draft=1' : ''}`,
  );
  return result.table;
}

export async function getTableLeaderboard(
  tableId: string,
  limit = 10,
  signal?: AbortSignal,
): Promise<TableLeaderboardData> {
  const safeLimit = Math.min(50, Math.max(3, Math.floor(limit)));
  const params = new URLSearchParams({ limit: String(safeLimit) });
  const result = await request<{ ok: true } & TableLeaderboardData>(
    `/api/tables/${encodeURIComponent(tableId)}/leaderboard?${params.toString()}`,
    { signal },
  );
  return {
    tableId: result.tableId,
    versionId: result.versionId,
    versionNumber: result.versionNumber,
    entries: result.entries,
  };
}

export async function createCommunityTable(definition: TableDefinition, description = ''): Promise<CommunityTableDetail> {
  const result = await request<{ ok: true; table: CommunityTableDetail }>('/api/tables', {
    method: 'POST',
    body: JSON.stringify({ definition, description }),
  });
  return result.table;
}

export async function updateCommunityTable(id: string, definition: TableDefinition, description?: string): Promise<CommunityTableDetail> {
  const result = await request<{ ok: true; table: CommunityTableDetail }>(`/api/tables/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify({ definition, description }),
  });
  return result.table;
}

export async function publishCommunityTable(id: string): Promise<{ id: string; number: number; hash: string }> {
  const result = await request<{ ok: true; version: { id: string; number: number; hash: string } }>(
    `/api/tables/${encodeURIComponent(id)}/publish`,
    { method: 'POST', body: '{}' },
  );
  return result.version;
}

export async function remixCommunityTable(id: string): Promise<CommunityTableDetail> {
  const result = await request<{ ok: true; table: CommunityTableDetail }>(`/api/tables/${encodeURIComponent(id)}/remix`, {
    method: 'POST',
    body: '{}',
  });
  return result.table;
}

export async function startTrackedPlay(tableId: string, tableVersionId?: string | null): Promise<string | null> {
  try {
    const result = await request<{ ok: true; playId: string }>('/api/plays/start', {
      method: 'POST',
      body: JSON.stringify({ tableId, tableVersionId: tableVersionId ?? undefined }),
    });
    return result.playId;
  } catch {
    return null;
  }
}

export async function finishTrackedPlay(
  playId: string | null,
  details: { durationMs: number; score: number; ballsUsed: number },
): Promise<void> {
  if (!playId) return;
  try {
    await request<{ ok: true }>(`/api/plays/${encodeURIComponent(playId)}/finish`, {
      method: 'POST',
      body: JSON.stringify(details),
    });
  } catch {
    // Gameplay must never fail because analytics/tracking is unavailable.
  }
}
